import { useState, type FormEvent } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { postAuthPasswordForgot, postAuthPasswordReset } from '@/api/auth';
import { errText, getChosenSchool } from '../shared';
import { AppSchoolHeader } from '@/components/AppSchoolHeader';

type Step = 'request' | 'reset';

export function ForgotPasswordPage() {
  const nav = useNavigate();
  const chosen = getChosenSchool();
  const [step, setStep] = useState<Step>('request');
  const [identifier, setIdentifier] = useState('');
  const [code, setCode] = useState('');
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  const requestCode = async (e: FormEvent) => {
    e.preventDefault();
    setBusy(true); setError(null); setNotice(null);
    try {
      await postAuthPasswordForgot({
        school_code: chosen?.code ?? undefined,
        identifier: identifier.trim(),
      });
      setNotice('If the account exists, a 6-digit code has been sent to the phone or email on file.');
      setStep('reset');
    } catch (err) {
      setError(errText(err, 'Could not send the code.'));
    } finally { setBusy(false); }
  };

  const resetPassword = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    if (password !== confirm) { setError('Passwords do not match.'); return; }
    if (password.length < 8) { setError('Password must be at least 8 characters.'); return; }
    setBusy(true);
    try {
      await postAuthPasswordReset({
        school_code: chosen?.code ?? undefined,
        identifier: identifier.trim(),
        code: code.trim(),
        password,
        password_confirmation: confirm,
      });
      nav('/login', { replace: true, state: { notice: 'Password updated. Please sign in.' } });
    } catch (err) {
      setError(errText(err, 'Could not reset the password.'));
    } finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen grid grid-cols-1 md:grid-cols-2 bg-white">
      <div className="hidden md:flex flex-col justify-center bg-[#0A2540] px-12 lg:px-20">
        <h1 className="text-4xl lg:text-5xl font-extrabold text-white tracking-tight">
          {chosen?.name ?? 'Myschool Portal'}
        </h1>
        <p className="text-slate-300 mt-4 text-base lg:text-lg">Reset your password</p>
      </div>
      <div className="flex flex-col justify-center px-6 py-12 md:px-12 lg:px-20">
        <div className="w-full max-w-md mx-auto">
          <AppSchoolHeader />
          <h2 className="text-3xl font-extrabold text-[#0A2540] mb-2">
            {step === 'request' ? 'Reset your password' : 'Set a new password'}
          </h2>
          <p className="text-sm text-slate-500 mb-8">
            {step === 'request'
              ? "Enter the phone, email or registration number on your account. We'll send a 6-digit code."
              : `Enter the code we sent and choose a new password.`}
          </p>

          {notice && <p className="text-sm text-green-700 mb-4" role="status">{notice}</p>}

          {step === 'request' ? (
            <form onSubmit={requestCode} className="space-y-5">
              <label className="block">
                <span className="block text-sm font-semibold text-[#0A2540] mb-1.5">
                  Phone, email or registration number
                </span>
                <input
                  className="w-full rounded-lg border border-slate-300 px-4 py-3 text-sm"
                  value={identifier}
                  onChange={(e) => setIdentifier(e.target.value)}
                  required autoComplete="username" placeholder="owner@school.ng"
                />
              </label>
              {error && <p className="text-sm text-red-600" role="alert">{error}</p>}
              <button type="submit" disabled={busy}
                className="w-full bg-[#0A2540] hover:bg-[#0f2f54] text-white font-semibold py-3.5 rounded-lg disabled:opacity-60">
                {busy ? 'Sending…' : 'Send code'}
              </button>
              <div className="text-center">
                <Link to="/login" className="text-sm text-slate-500 hover:text-[#0A2540] hover:underline">
                  Back to sign in
                </Link>
              </div>
            </form>
          ) : (
            <form onSubmit={resetPassword} className="space-y-5">
              <label className="block">
                <span className="block text-sm font-semibold text-[#0A2540] mb-1.5">6-digit code</span>
                <input
                  className="w-full rounded-lg border border-slate-300 px-4 py-3 text-lg tracking-[0.5em] text-center font-bold"
                  value={code}
                  onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                  required inputMode="numeric" maxLength={6} placeholder="000000"
                />
              </label>
              <label className="block">
                <span className="block text-sm font-semibold text-[#0A2540] mb-1.5">New password</span>
                <input
                  className="w-full rounded-lg border border-slate-300 px-4 py-3 text-sm"
                  type="password" value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required minLength={8} autoComplete="new-password"
                />
              </label>
              <label className="block">
                <span className="block text-sm font-semibold text-[#0A2540] mb-1.5">Confirm password</span>
                <input
                  className="w-full rounded-lg border border-slate-300 px-4 py-3 text-sm"
                  type="password" value={confirm}
                  onChange={(e) => setConfirm(e.target.value)}
                  required minLength={8} autoComplete="new-password"
                />
              </label>
              {error && <p className="text-sm text-red-600" role="alert">{error}</p>}
              <button type="submit" disabled={busy}
                className="w-full bg-[#0A2540] hover:bg-[#0f2f54] text-white font-semibold py-3.5 rounded-lg disabled:opacity-60">
                {busy ? 'Saving…' : 'Set new password'}
              </button>
              <div className="text-center">
                <button type="button"
                  onClick={() => { setStep('request'); setError(null); setNotice(null); }}
                  className="text-sm text-slate-500 hover:text-[#0A2540] hover:underline">
                  Didn't get a code? Try again
                </button>
              </div>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
